import { forwardRef, useState } from "react"
import PropTypes from "prop-types"
import MDButtonRoot from "components/MDButton/MDButtonRoot"
import CircularProgress from "@mui/material/CircularProgress"
import { useMaterialUIController } from "context"

const MDButton = forwardRef(
  ({ color, variant, size, circular, iconOnly, children, onClick, ...rest }, ref) => {
    const [loading, setLoading] = useState(false)
    const [controller] = useMaterialUIController()
    const { darkMode } = controller

    const handleClick = async (event) => {
      if (loading) return // Prevent multiple clicks
      setLoading(true)

      try {
        if (onClick) {
          await onClick(event) // Call the parent’s onClick function
        }
      } catch (error) {
        console.error("Error in button action:", error)
      } finally {
        setLoading(false)
      }
    }

    return (
      <MDButtonRoot
        {...rest}
        ref={ref}
        color={color}
        variant={variant === "gradient" ? "contained" : variant}
        size={size}
        ownerState={{ color, variant, size, circular, iconOnly, darkMode }}
        disabled={loading || rest.disabled} // Disable when loading
        onClick={handleClick}
      >
        {loading ? <CircularProgress size={20} color="inherit" /> : children}
      </MDButtonRoot>
    )
  }
)

MDButton.defaultProps = {
  size: "medium",
  variant: "contained",
  color: "white",
  circular: false,
  iconOnly: false,
  onClick: undefined,
}

MDButton.propTypes = {
  size: PropTypes.oneOf(["small", "medium", "large"]),
  variant: PropTypes.oneOf(["text", "contained", "outlined", "gradient"]),
  color: PropTypes.oneOf([
    "white",
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "light",
    "dark",
  ]),
  circular: PropTypes.bool,
  iconOnly: PropTypes.bool,
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func, // Not required anymore
}

export default MDButton
